import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 25;

const renderDnColumn = (dnString) => {
  if (!dnString) return '-';
  const dnList = dnString.split(', ');
  if (dnList.length === 1) {
    return <span title={dnString}>{dnList[0]}</span>;
  }
  return (
    <span className="cursor-help text-blue-600 hover:text-blue-800 font-medium" title={dnString}>
      {dnList[0]} <span className="text-xs text-gray-500 font-normal">({`+${dnList.length - 1} more`})</span>
    </span>
  );
};

const SaleContractList = () => {
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0); // ✅ Grand total from backend
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    contractNo: '',
    purchaseContractNo: '',
    contractDateFrom: '',
    contractDateTo: '',
    buyer: '',
    commodity: '',
    vesselName: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line
  }, [page, debouncedSearch, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      contractNo: '',
      purchaseContractNo: '',
      contractDateFrom: '',
      contractDateTo: '',
      buyer: '',
      commodity: '',
      vesselName: ''
    });
    setSearch('');
    setPage(1);
  };

 const fetchContracts = async () => {
  setLoading(true);
  setError(null);
  try {
    const params = new URLSearchParams({
      page,
      limit: ROWS_PER_PAGE,
      search: debouncedSearch.trim()
    });

    if (filters.contractNo) {
      params.append('contractNo', filters.contractNo);
    }
    if (filters.purchaseContractNo) {
      params.append('purchaseContractNo', filters.purchaseContractNo);
    }
    if (filters.contractDateFrom) {
      params.append('contractDateFrom', filters.contractDateFrom);
    }
    if (filters.contractDateTo) {
      params.append('contractDateTo', filters.contractDateTo);
    }
    if (filters.buyer) {
      params.append('buyer', filters.buyer);
    }
    if (filters.commodity) {
      params.append('commodity', filters.commodity);
    }
    if (filters.vesselName) {
      params.append('vesselName', filters.vesselName);
    }

    // ✅ Debug log
    console.log('=== FRONTEND REQUEST ===');
    console.log('URL Params:', params.toString());
    console.log('Current Page:', page);

    const res = await fetch(`${API_BASE_URL}/api/sales/sale-contract-get?${params}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch');
    const data = await res.json();
    
    // ✅ Debug log
    console.log('=== FRONTEND RESPONSE ===');
    console.log('Total Records:', data.total);
    console.log('Total Quantity from Backend:', data.totalQuantity);
    console.log('Number of contracts received:', data.data?.length);
    
    const uniqueContracts = [];
    const seen = new Set();
    for (const c of data.data || []) {
      if (!seen.has(c.sales_contract_id)) {
        uniqueContracts.push(c);
        seen.add(c.sales_contract_id);
      }
    }
    
    // ✅ Manual calculation for verification
    const manualTotal = uniqueContracts.reduce((sum, contract) => {
      return sum + (parseFloat(contract.contract_quantity_mt) || 0);
    }, 0);
    console.log('Manual Total (current page only):', manualTotal.toFixed(2));
    
    setContracts(uniqueContracts);
    setTotal(data.total || 0);
    setTotalQuantity(data.totalQuantity || 0);
    
    console.log('State Updated - Total Quantity:', data.totalQuantity);
  } catch (error) {
    setError('Failed to load data');
    console.error('Fetch sale contract error:', error);
  } finally {
    setLoading(false);
  }
};


  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  // ✅ Check if we're on the last page
  const isLastPage = page === totalPages;

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Sales Contract Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Quick Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-medium border ${
              showFilters 
                ? 'bg-gray-600 text-white border-gray-700 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={() => navigate('/sale-contract/new')}
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sales Contract No.</label>
              <input
                type="text"
                name="contractNo"
                placeholder="Enter Sales Contract No."
                value={filters.contractNo}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Purchase Contract No.</label>
              <input
                type="text"
                name="purchaseContractNo"
                placeholder="Enter Purchase Contract No."
                value={filters.purchaseContractNo}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Buyer</label>
              <input
                type="text"
                name="buyer"
                placeholder="Search buyer..."
                value={filters.buyer}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
              <input
                type="text"
                name="commodity"
                placeholder="Search commodity..."
                value={filters.commodity}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Name</label>
              <input
                type="text"
                name="vesselName"
                placeholder="Search vessel..."
                value={filters.vesselName}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <button
              onClick={resetFilters}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : contracts.length === 0 ? (
        <NoRecordFound message="No sales contract records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Purchase Contract No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Sales Contract No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contract Date</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Trade Date</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type of Transaction</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">TEL</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Buyer</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Commodity</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Quantity (MT)</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Sale Price/MT</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">EMD</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Delivery Point</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Delivery Start</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Delivery End</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Broker</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Vessel</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">DN of Account</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Closing Date</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Created At</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c, i) => (
                <tr key={c.sales_contract_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 whitespace-nowrap">{c.purchase_contract_no || '-'}</td>
                  <td className="px-3 py-1 font-semibold whitespace-nowrap">{c.sales_contract_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(c.contract_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(c.trade_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.type_of_transaction}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.tel}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.domestic_buyer_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.commodity_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.contract_quantity_mt}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.sale_price_per_mt}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.emd !== null && c.emd !== undefined && c.emd !== '' ? parseFloat(c.emd).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-'}</td>
                  <td className="px-3 py-1">
                    <div 
                      className="truncate max-w-32 whitespace-nowrap" 
                      title={c.warehouse_name ? `${c.warehouse_name}${c.warehouse_location ? ' (' + c.warehouse_location + ')' : ''}` : c.delivery_point}
                    >
                      {c.warehouse_name ? `${c.warehouse_name}${c.warehouse_location ? ' (' + c.warehouse_location + ')' : ''}` : c.delivery_point}
                    </div>
                  </td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(c.delivery_start_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(c.delivery_end_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.broker_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.vessel_name || '-'}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{renderDnColumn(c.dn_of_account)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{formatDateDDMMYY(c.closing_date)}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{c.created_at ? c.created_at.substring(0, 10) : ''}</td>
                  <td className="px-3 py-1 whitespace-nowrap">
                    <button
                      className="bg-[#3899cf] hover:bg-[#0e8dc6] text-white px-2 py-1 rounded text-xs mr-2"
                      onClick={() => navigate(`/sale-contract/view/${c.sales_contract_id}`)}
                    >
                      View
                    </button>
                    <button
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded text-xs"
                      onClick={() => navigate(`/sale-contract/edit/${c.sales_contract_id}`)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* ✅ Show footer ONLY on last page */}
            {isLastPage && (
              <tfoot className="bg-gray-100">
                <tr className="font-semibold">
                  <td colSpan="7" className="px-3 py-2 text-right">Grand Total Quantity:</td>
                  <td className="px-3 py-2 font-semibold">{totalQuantity} MT</td>
                  <td colSpan="11"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SaleContractList;
